import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { frontendMenuService } from '../../services/frontendMenuService';
import { ppidContentService } from '../../services/ppidContentService';
import {
    FaDownload, FaFilePdf, FaCalendarAlt, FaSearch, FaInfoCircle,
    FaFileAlt, FaEye, FaSpinner, FaFileImage, FaFileWord,
    FaFileExcel, FaFolderOpen
} from 'react-icons/fa';

import { getAssetUrl } from '../../services/api';

const PPIDPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');

    const [ppidMenus, setPpidMenus] = useState([]);
    const [activeCategory, setActiveCategory] = useState('');
    const [contents, setContents] = useState([]);
    const [currentMenu, setCurrentMenu] = useState(null);
    const [loadingMenus, setLoadingMenus] = useState(true);
    const [loadingContents, setLoadingContents] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState('');

    // State untuk filter tahun per kategori dokumen
    // Format: { 'RFK': '2026', 'RENJA': '2025', ... }
    const [activeTahunPerKategori, setActiveTahunPerKategori] = useState({});

    // Load PPID menus
    useEffect(() => {
        loadPPIDMenus();
    }, []);

    // Load contents saat tabParam berubah
    useEffect(() => {
        if (tabParam) {
            setActiveCategory(tabParam);
            loadContentsBySlug(tabParam);
        }
    }, [tabParam]);

    const loadPPIDMenus = async () => {
        setLoadingMenus(true);
        try {
            const result = await frontendMenuService.getPublicMenus();
            if (result.success) {
                const menus = findPPIDMenus(result.data);
                setPpidMenus(menus);

                if (!tabParam && menus.length > 0) {
                    const firstMenu = menus[0];
                    setSearchParams({ tab: firstMenu.slug }, { replace: true });
                }
            }
        } catch (error) {
            console.error('Error loading menus:', error);
        }
        setLoadingMenus(false);
    };

    const loadContentsBySlug = async (slug) => {
        setLoadingContents(true);
        setError('');
        setContents([]);
        setActiveTahunPerKategori({}); // Reset filter tahun saat ganti menu

        try {
            const menu = ppidMenus.find(m => m.slug === slug);
            setCurrentMenu(menu || null);

            const result = await ppidContentService.getContentsBySlug(slug);

            if (result.success) {
                const contentData = result.data?.contents || result.data?.data || [];
                setContents(Array.isArray(contentData) ? contentData : []);
            } else {
                setError(result.message || 'Gagal memuat konten');
                setContents([]);
            }
        } catch (error) {
            console.error('Error in loadContentsBySlug:', error);
            setError('Terjadi kesalahan saat memuat konten');
            setContents([]);
        } finally {
            setLoadingContents(false);
        }
    };

    const findPPIDMenus = (menuTree, parent = null) => {
        let result = [];
        for (const menu of menuTree) {
            const isPPID = menu.slug === 'ppid' ||
                menu.name.toLowerCase().includes('ppid') ||
                menu.route?.includes('/informasi/ppid') ||
                menu.slug?.startsWith('ppid-');

            if (isPPID) {
                result.push({
                    ...menu,
                    parent_name: parent?.name || menu.name
                });
            }

            if (menu.children && menu.children.length > 0) {
                result = [...result, ...findPPIDMenus(menu.children, menu)];
            }
        }
        return result;
    };

    const handleCategoryClick = (menu) => {
        setActiveCategory(menu.slug);
        setCurrentMenu(menu);
        setSearchParams({ tab: menu.slug });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getFileIcon = (fileType) => {
        switch (fileType?.toLowerCase()) {
            case 'pdf': return <FaFilePdf className="text-red-500 text-2xl" />;
            case 'jpg': case 'jpeg': case 'png': return <FaFileImage className="text-blue-500 text-2xl" />;
            case 'doc': case 'docx': return <FaFileWord className="text-blue-700 text-2xl" />;
            case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600 text-2xl" />;
            default: return <FaFileAlt className="text-gray-500 text-2xl" />;
        }
    };

    const filteredContents = contents.filter(content =>
        content.judul?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        content.deskripsi?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Group by Kategori Dokumen -> Tahun -> Judul
    const groupByKategoriDokumen = (contents) => {
        return contents.reduce((acc, item) => {
            const kategoriDok = item.kategori_dokumen || 'LAINNYA';
            const tahun = item.tahun || 'Tidak Ada Tahun';
            const judul = item.judul || 'Tanpa Judul';

            if (!acc[kategoriDok]) acc[kategoriDok] = {};
            if (!acc[kategoriDok][tahun]) acc[kategoriDok][tahun] = {};
            if (!acc[kategoriDok][tahun][judul]) acc[kategoriDok][tahun][judul] = [];
            acc[kategoriDok][tahun][judul].push(item);
            return acc;
        }, {});
    };

    const groupedData = groupByKategoriDokumen(filteredContents);

    // Handler untuk memilih tahun pada kategori tertentu
    const handleTahunClick = (kategori, tahun) => {
        setActiveTahunPerKategori(prev => ({
            ...prev,
            [kategori]: prev[kategori] === tahun ? '' : tahun
        }));
    };

    // Render konten yang dikelompokkan
    const renderGroupedContent = () => {
        if (loadingContents) {
            return (
                <div className="text-center py-8">
                    <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto mb-3" />
                    <p className="text-gray-500">Memuat konten...</p>
                </div>
            );
        }

        if (Object.keys(groupedData).length === 0) {
            return (
                <div className="text-center py-8 text-gray-500 bg-white rounded-xl shadow-sm">
                    <FaFileAlt className="text-4xl text-gray-300 mx-auto mb-3" />
                    Belum ada informasi untuk kategori ini
                </div>
            );
        }

        const sortedKategori = Object.keys(groupedData).sort();

        return (
            <div className="space-y-8">
                {sortedKategori.map((kategoriDok) => {
                    const yearsData = groupedData[kategoriDok];
                    const allYearsInKategori = Object.keys(yearsData).sort((a, b) => b - a);
                    const selectedTahun = activeTahunPerKategori[kategoriDok] || '';

                    // Tentukan tahun yang akan ditampilkan
                    const yearsToShow = selectedTahun
                        ? { [selectedTahun]: yearsData[selectedTahun] }
                        : yearsData;

                    // Skip jika filter aktif tapi tidak ada data
                    if (selectedTahun && !yearsData[selectedTahun]) return null;

                    return (
                        <div key={kategoriDok} className="bg-white rounded-xl shadow-sm overflow-hidden">
                            {/* Kategori Dokumen Header */}
                            <div className="bg-[#0F4C3A] px-6 py-4">
                                <h3 className="text-xl font-bold text-white uppercase tracking-wide">
                                    {kategoriDok}
                                </h3>
                            </div>

                            {/* Tab Tahun - DI DALAM KATEGORI */}
                            <div className="border-b border-gray-200 px-6">
                                <div className="flex flex-wrap gap-2 py-3">
                                    {allYearsInKategori.map(tahun => (
                                        <button
                                            key={tahun}
                                            onClick={() => handleTahunClick(kategoriDok, tahun)}
                                            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${selectedTahun === tahun
                                                    ? 'bg-[#2E7D32] text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            Tahun {tahun}
                                        </button>
                                    ))}
                                    {selectedTahun && (
                                        <button
                                            onClick={() => handleTahunClick(kategoriDok, selectedTahun)}
                                            className="px-3 py-2 text-xs text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            ✕ Reset
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                {Object.entries(yearsToShow).map(([tahun, judulData]) => {
                                    if (!judulData || Object.keys(judulData).length === 0) return null;

                                    return (
                                        <div key={tahun}>
                                            {/* Tahun Header */}
                                            <div className="flex items-center mb-4">
                                                <span className="bg-[#2E7D32] text-white px-4 py-1.5 rounded-full text-sm font-bold">
                                                    Tahun {tahun}
                                                </span>
                                                <div className="flex-1 h-px bg-gray-200 ml-4"></div>
                                            </div>

                                            {/* Judul Groups */}
                                            <div className="space-y-6">
                                                {Object.entries(judulData).map(([judul, items]) => (
                                                    <div key={judul} className="border-l-4 border-[#2E7D32] pl-4">
                                                        <h4 className="text-lg font-semibold text-[#0F4C3A] mb-3">
                                                            {judul}
                                                        </h4>

                                                        <div className="space-y-3">
                                                            {items.map((content) => (
                                                                <div
                                                                    key={content.id}
                                                                    className="flex flex-col md:flex-row md:items-center justify-between bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                                                                >
                                                                    <div className="flex-1 flex items-start">
                                                                        <div className="mr-4 mt-1">
                                                                            {getFileIcon(content.file_type)}
                                                                        </div>
                                                                        <div>
                                                                            {content.deskripsi && (
                                                                                <p className="text-gray-600 text-sm mb-2">
                                                                                    {content.deskripsi}
                                                                                </p>
                                                                            )}
                                                                            <div className="flex flex-wrap items-center text-sm text-gray-500 gap-4">
                                                                                {content.published_at && (
                                                                                    <span className="flex items-center">
                                                                                        <FaCalendarAlt className="mr-1" />
                                                                                        {formatDate(content.published_at)}
                                                                                    </span>
                                                                                )}
                                                                                {content.file_name && (
                                                                                    <span>
                                                                                        {content.file_name} ({formatFileSize(content.file_size)})
                                                                                    </span>
                                                                                )}
                                                                                {content.views > 0 && (
                                                                                    <span className="flex items-center">
                                                                                        <FaEye className="mr-1" />
                                                                                        {content.views} views
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-3 mt-3 md:mt-0 md:ml-4">
                                                                        {content.image_path && (
                                                                            <img
                                                                                src={getAssetUrl(content.image_path)}
                                                                                alt={content.judul}
                                                                                className="w-16 h-16 object-cover rounded-lg"
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {content.file_path && (
                                                                            <>
                                                                                <a
                                                                                    href={getAssetUrl(content.file_path)}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-[#2E7D32] hover:text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 border border-[#2E7D32] hover:bg-[#2E7D32] transition-colors"
                                                                                >
                                                                                    <FaEye />
                                                                                    Preview
                                                                                </a>
                                                                                <a
                                                                                    href={getAssetUrl(content.file_path)}
                                                                                    download
                                                                                    className="bg-[#2E7D32] hover:bg-[#4CAF50] text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 transition-colors"
                                                                                >
                                                                                    <FaDownload />
                                                                                    Download
                                                                                </a>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                    <FaInfoCircle className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            PPID - Pejabat Pengelola Informasi dan Dokumentasi
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Layanan informasi publik BPKAD Kota Magelang
                        </p>
                    </div>
                </div>
            </div>

            {/* Kategori Navigation */}
            {loadingMenus ? (
                <div className="text-center py-8">
                    <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto mb-3" />
                    <p className="text-gray-500">Memuat menu...</p>
                </div>
            ) : ppidMenus.length > 0 ? (
                <div className="flex flex-wrap gap-3 mb-8">
                    {ppidMenus.map((menu) => (
                        <button
                            key={menu.id}
                            onClick={() => handleCategoryClick(menu)}
                            className={`px-5 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 ${activeCategory === menu.slug
                                    ? 'bg-[#0F4C3A] text-white shadow-lg'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                                }`}
                        >
                            <FaFolderOpen className="text-lg" />
                            <span className="font-semibold text-sm">{menu.name}</span>
                        </button>
                    ))}
                </div>
            ) : null}

            {/* Search Bar */}
            {currentMenu && (
                <div className="mb-6">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cari informasi..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        />
                    </div>
                </div>
            )}

            {/* Content Area */}
            {currentMenu && (
                <div>
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-[#0F4C3A] mb-2">
                            {currentMenu.name}
                        </h2>
                        <p className="text-gray-600">
                            Informasi publik untuk {currentMenu.name}
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-50 p-4 rounded-lg mb-4">
                            <p className="text-red-600">{error}</p>
                        </div>
                    )}

                    {currentMenu.slug?.includes('permohonan') ? (
                        <div className="card-glass p-6">
                            <h3 className="text-xl font-bold text-[#0F4C3A] mb-4">
                                Prosedur Permohonan Informasi
                            </h3>
                            <ol className="list-decimal list-inside space-y-3 text-gray-700">
                                <li>Pemohon mengisi formulir permohonan informasi</li>
                                <li>Melampirkan fotokopi KTP/identitas pemohon</li>
                                <li>Menyampaikan secara langsung atau melalui email</li>
                                <li>Petugas PPID memproses permohonan (maks. 10 hari kerja)</li>
                                <li>Informasi disampaikan atau ditolak dengan alasan</li>
                            </ol>
                        </div>
                    ) : (
                        renderGroupedContent()
                    )}
                </div>
            )}
        </div>
    );
};

export default PPIDPage;